import { AxiosResponse } from "axios";
import { Api } from "services/api/api.service";
import { MonthSalaryDispatch } from "./monthSalaryStore";
import { MonthSalariesActions } from "model/Salary";

const getAllSalaries =
  (dispatch: MonthSalaryDispatch) =>
  async (
    companyId: number,
    page: number,
    pageSize: number
  ): Promise<AxiosResponse<any> | void> => {
    dispatch({ type: "SET_IS_LOADING", payload: true });

    try {
      const res = await Api().company.salary.getAllSalaries(
        companyId,
        page,
        pageSize
      );
      dispatch({
        type: MonthSalariesActions.GetAllSalaries,
        payload: { workersWithSalary: res.data.workers, total: res.data.total },
      });
      dispatch({ type: "SET_IS_LOADING", payload: false });
      return res;
    } catch (e: any) {
      console.error("Error when getting month salaries", e);
      dispatch({
        type: "SET_REQUEST_ERROR",
        payload: { message: "Failed to fetch salaries", err: e },
      });
      dispatch({ type: "SET_IS_LOADING", payload: false });
      return Promise.reject(e);
    }
  };

const clearSalaries =
  (dispatch: MonthSalaryDispatch) =>
  async (): Promise<AxiosResponse<any> | void> => {
    dispatch({ type: "SET_IS_LOADING", payload: true });

    try {
      dispatch({
        type: MonthSalariesActions.ClearSalaries,
        payload: null,
      });
      dispatch({ type: "SET_IS_LOADING", payload: false });
    } catch (e: any) {
      dispatch({ type: "SET_IS_LOADING", payload: false });
      return Promise.reject(e);
    }
  };

const updateWorkerEmployment =
  (dispatch: MonthSalaryDispatch) =>
  async (
    companyId: number,
    workerId: number,
    startDate: string,
    employmentType: string,
    salary: number,
    salaryType: string,
    workPercentage: number,
    taxTable: number,
    paymentType: string,
    role: string,
    endDate: string | null,
    employeeCategory: string,
    employeeNumber: number
  ): Promise<AxiosResponse<any> | void> => {
    dispatch({ type: "SET_IS_LOADING", payload: true });
    try {
      const res = await Api()
        .company.salary.updateWorkerEmployment(
          companyId,
          workerId,
          startDate,
          employmentType,
          salary,
          salaryType,
          workPercentage,
          taxTable,
          paymentType,
          role,
          endDate,
          employeeCategory,
          employeeNumber
        )
        .then((res: any) => {
          dispatch({
            type: MonthSalariesActions.UpdateWorkerSalary,
            payload: { workerWithSalary: res.data },
          });
          dispatch({ type: "SET_IS_LOADING", payload: false });

          return Promise.resolve(res);
        });
    } catch (e: any) {
      let errorMessage = "Unknown error occurred";

      if (e.response && e.response.data) {
        errorMessage = e.response.data.errorMessage;
      }

      dispatch({
        type: "SET_REQUEST_ERROR",
        payload: { message: errorMessage, err: e },
      });

      return Promise.reject({
        ...e,
        errorMessage,
      });
    }
  };

const generateHogiaFile =
  (dispatch: MonthSalaryDispatch) =>
  async (
    companyId: number,
    year: number,
    month: number
  ): Promise<AxiosResponse<Blob>> => {
    dispatch({ type: "SET_IS_LOADING", payload: true });

    try {
      const res = await Api().company.salary.generateHogiaFile(
        companyId,
        year,
        month
      );
      return res;
    } catch (e: any) {
      let errorMessage = "Unknown error occurred";
      const data = e.response?.data;

      if (
        data &&
        typeof data === "object" &&
        !(data instanceof Blob) &&
        "errorMessage" in data
      ) {
        errorMessage = (data as any).errorMessage;
      }

      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          errorMessage = json?.errorMessage ?? errorMessage;
        } catch {
          try {
            errorMessage = await data.text();
          } catch {}
        }
      }

      dispatch({
        type: "SET_REQUEST_ERROR",
        payload: { message: errorMessage, err: e },
      });

      throw { ...e, errorMessage };
    } finally {
      dispatch({ type: "SET_IS_LOADING", payload: false });
    }
  };

const generateSIEFile =
  (dispatch: MonthSalaryDispatch) =>
  async (
    companyId: number,
    year: number,
    month: number
  ): Promise<AxiosResponse<Blob>> => {
    dispatch({ type: "SET_IS_LOADING", payload: true });

    try {
      const res = await Api().company.salary.generateSIEFile(
        companyId,
        year,
        month
      );
      return res;
    } catch (e: any) {
      let errorMessage = "Unknown error occurred";
      const data = e.response?.data;

      if (
        data &&
        typeof data === "object" &&
        !(data instanceof Blob) &&
        "errorMessage" in data
      ) {
        errorMessage = (data as any).errorMessage;
      }
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          errorMessage = json?.errorMessage ?? errorMessage;
        } catch {
          try {
            errorMessage = await data.text();
          } catch {}
        }
      }

      dispatch({
        type: "SET_REQUEST_ERROR",
        payload: { message: errorMessage, err: e },
      });

      throw { ...e, errorMessage };
    } finally {
      dispatch({ type: "SET_IS_LOADING", payload: false });
    }
  };

export const monthSalaryActions = (dispatch: MonthSalaryDispatch) => ({
  getAllSalaries: getAllSalaries(dispatch),
  updateWorkerEmployment: updateWorkerEmployment(dispatch),
  clearSalaries: clearSalaries(dispatch),
  generateHogiaFile: generateHogiaFile(dispatch),
  generateSIEFile: generateSIEFile(dispatch),
});
